import { Box, Flex, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import parse from 'html-react-parser';
import React from 'react';
import styled from 'styled-components';
import { CoverageItemDescription, CoverageItemImage, ModernCoverageItemTitle } from './StyledElements';
import {
  CoverageItemDescriptionText,
  CoverageItemTitleText,
  StyledPill,
  StyledSection,
  StyledTitle,
  Subtitle,
} from './CoverageShared';
import type { CoverageSectionProps } from './CoverageShared';

const ModernCoverageContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  row-gap: 32px;
  margin: 36px auto 80px auto;
  padding: 0 20px;
  width: 100%;
  max-width: 500px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    padding: 0;
    max-width: none;
    margin: 48px auto 80px auto;
  `}

  ${MEDIA_QUERIES.tablet`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    column-gap: 72px;
    row-gap: 48px;
    max-width: ${280 * 2 + 72}px;
  `}

  ${MEDIA_QUERIES.desktop`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    column-gap: 72px;
    row-gap: 48px;
    max-width: ${280 * 3 + 72 * 2}px;
  `}
`;

const ModernCoverageItem = styled(Flex)`
  width: 100%;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  max-width: 335px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 280px;
    flex-direction: column;
    align-items: center;
    gap: 0;
  `}
`;

const ModernCoverageItemInfo = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    align-items: center;
    gap: 7px;
  `}

  ${MEDIA_QUERIES.mobile`
    > :first-child {
    margin-top: 8px !important;
  }
  `}
`;

const ModernCoverageItemImage = styled(CoverageItemImage)`
  min-width: 100px;
  width: 100px;
  height: 100px;
  flex-shrink: 0;

  ${MEDIA_QUERIES.tabletAndDesktop`
    min-width: 165px;
    width: 165px;
    height: 165px;
  `}
`;

const ModernCoverageItemTitleText = styled(CoverageItemTitleText)`
  text-align: left;
  font-size: 1.6rem;

  ${MEDIA_QUERIES.tabletAndDesktop`
    text-align: center;
    font-size: 2rem;
  `}
`;

const ModernCoverageItemDescriptionText = styled(CoverageItemDescriptionText)`
  font-size: 1.4rem;
  white-space: pre-line;
  text-align: left;

  ${MEDIA_QUERIES.tabletAndDesktop`
    text-align: center;
    max-width: 280px;
  `}
`;

const ModernStyledTitle = styled(StyledTitle)`
  ${MEDIA_QUERIES.mobile`
    max-width: 335px;
    `}
`;

export const ModernVariantCoverage = React.forwardRef<HTMLDivElement, CoverageSectionProps>(
  ({ title, subtitle, coverages, ...props }, ref) => {
    const { isMobileViewport } = useViewportSize();
    const renderCoverageItems = () =>
      coverages.map(({ background, backgroundDesktop, title, description, alt, pill, preElement: sideNote }) => {
        const titles = typeof title === 'string' ? [title] : title;
        const descriptions = typeof description === 'string' ? [description] : description;

        return (
          <ModernCoverageItem key={titles.join()} position="relative" role="listitem">
            {pill && <StyledPill backgroundColor={pill.backgroundColor}>{pill.text}</StyledPill>}
            {sideNote}
            <ModernCoverageItemImage
              alt={alt}
              src={(isMobileViewport ? background : backgroundDesktop) || background}
              width={165}
              height={165}
            />
            <ModernCoverageItemInfo tabIndex={0}>
              <ModernCoverageItemTitle flexDirection="column">
                {titles.map(t => (
                  <ModernCoverageItemTitleText key={t} $variant="modern">
                    {parse(t)}
                  </ModernCoverageItemTitleText>
                ))}
              </ModernCoverageItemTitle>
              <CoverageItemDescription flexDirection="column">
                {descriptions.map(d => (
                  <ModernCoverageItemDescriptionText key={d}>{parse(d)}</ModernCoverageItemDescriptionText>
                ))}
              </CoverageItemDescription>
            </ModernCoverageItemInfo>
          </ModernCoverageItem>
        );
      });

    return (
      <StyledSection ref={ref} mb={0} pt={[45, 100]} width={1} contain="paint" {...props}>
        <Flex gap="8px" flexDirection="column" alignItems="center">
          <ModernStyledTitle tabIndex={0}>{parse(title)}</ModernStyledTitle>
          <Box width={['80%', '100%']} mx="auto">
            <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
          </Box>
        </Flex>

        <ModernCoverageContainer role="list">{renderCoverageItems()}</ModernCoverageContainer>
      </StyledSection>
    );
  },
);
